import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Suspense, lazy } from "react";
import { WagmiProvider } from 'wagmi';
import { config } from './wagmi';
import { MainLayout } from "./components/layout/MainLayout";
import { featureFlags } from "./config/featureFlags";

// Lazy Load Pages
const Index = lazy(() => import("./pages/Index"));
const Swap = lazy(() => import("./pages/Swap"));
const Pools = lazy(() => import("./pages/Pools"));
const PoolDetails = lazy(() => import("./pages/PoolDetails"));
const Portfolio = lazy(() => import("./pages/Portfolio"));
const History = lazy(() => import("./pages/History"));
const Settings = lazy(() => import("./pages/Settings"));
const Status = lazy(() => import("./pages/Status"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Bet = lazy(() => import("./pages/Bet"));
const MatchDetail = lazy(() => import("./pages/MatchDetail"));
const Docs = lazy(() => import("./pages/Docs"));
const Markets = lazy(() => import("./pages/Markets"));
const MarketDetail = lazy(() => import("./pages/MarketDetail"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

// Loading Spinner Component
const LoadingSpinner = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
  </div>
);

const App = () => (
  <WagmiProvider config={config}>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <MainLayout>
            <Suspense fallback={<LoadingSpinner />}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/swap" element={<Swap />} />
                <Route path="/pools" element={<Pools />} />
                <Route path="/pool/:address" element={<PoolDetails />} />
                <Route path="/portfolio" element={<Portfolio />} />
                <Route path="/history" element={<History />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/status" element={<Status />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/bet" element={<Bet />} />
                <Route path="/bet/:matchId" element={<MatchDetail />} />
                <Route path="/docs" element={<Docs />} />
                {/* FluxMarkets routes — gated by feature flag */}
                <Route
                  path="/markets"
                  element={featureFlags.MARKETS_DISCOVERY ? <Markets /> : <Navigate to="/bet" replace />}
                />
                <Route
                  path="/markets/:id"
                  element={featureFlags.MARKETS_DISCOVERY ? <MarketDetail /> : <Navigate to="/bet" replace />}
                />
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </MainLayout>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </WagmiProvider>
);

export default App;

